import React, { useEffect, useState } from 'react'
import { Grid, Typography, Button, makeStyles, Box } from '@material-ui/core'
import useTranslate from '../../../i18n/useTranslate'
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded'
import { DocumentSop, useResearcherDocument, DocumentDownloadGuide } from '../../../services/um/useResearcherDocument'
import MyAccordionCtrl from '../../../control/accordion/accordion'

type Props = {
    dataList: DocumentSop[]
    /** REC ที่กำลังดูอยู่ — คำแนะนำต้องเป็นของ REC เดียวกับเอกสาร */
    rec?: number | string
    resetKey?: number
    onDownloadGroup?: (group: DocumentSop) => void
}

const GroupedSopList: React.FC<Props> = ({ dataList, rec, resetKey = 0, onDownloadGroup }) => {
    const classes = useStyles()
    const translate = useTranslate()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'
    const { getDocumentDownloadGuide } = useResearcherDocument()

    const [documentDownloadGuides, setDocumentDownloadGuides] = useState<DocumentDownloadGuide[]>([])

    useEffect(() => {
        const fetchDocumentDownloadGuide = async () => {
            try {
                const data = await getDocumentDownloadGuide({ rec })
                setDocumentDownloadGuides(data ?? [])
            } catch (error) {
                console.error('Error while fetching document download guide')
            }
        }

        fetchDocumentDownloadGuide()
    }, [rec])

    const hasGuideMapping = documentDownloadGuides.some((e) => e.projectSubmitTypeID != null)
    const findGuide = (groupID: number) =>
        documentDownloadGuides.find((e) =>
            hasGuideMapping ? e.projectSubmitTypeID === groupID : e.documentDownloadGuideID === groupID,
        )

    return (
        <Box>
            {dataList?.map((group) => {
                const groupName = lang === 'th' ? group.nameTh || group.nameEn : group.nameEn || group.nameTh
                const groupHasAnyFile = group.files?.some((f) => f.documentTh?.path || f.documentEn?.path)
                const guideContent = findGuide(group.id)?.content?.[lang]
                const guideForName =
                    lang === 'th' ? group.nameTh || group.nameEn : group.nameEn?.toLowerCase() || group.nameTh

                return (
                    <Box key={`${resetKey}-${group.id}`} mb={2}>
                        <MyAccordionCtrl
                            defaultExpanded={false}
                            renderTitle={
                                <Grid
                                    container
                                    justify="space-between"
                                    alignItems="center"
                                    wrap="nowrap"
                                    className={classes.titleRow}
                                >
                                    <Grid item>
                                        <Typography variant="h6" className={classes.groupTitle}>
                                            {groupName}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            startIcon={<GetAppRoundedIcon />}
                                            disabled={!groupHasAnyFile}
                                            onClick={(event) => {
                                                // กันไม่ให้กดปุ่มแล้วไปพับ/กาง accordion
                                                event.stopPropagation()
                                                onDownloadGroup?.(group)
                                            }}
                                            onFocus={(event) => event.stopPropagation()}
                                        >
                                            {translate.get('BUTTON.DOWNLOAD_ALL')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            }
                        >
                            <Grid container spacing={2}>
                                {guideContent ? (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" className={classes.guideTitle}>
                                            {`${translate.get('UM.RESEARCHER_DOC.GUIDE_FOR')}${guideForName}`}
                                        </Typography>
                                        <div
                                            className={classes.guide}
                                            dangerouslySetInnerHTML={{ __html: guideContent }}
                                        />
                                    </Grid>
                                ) : null}

                                {group.files?.map((file) => {
                                    const th = file.documentTh
                                    const en = file.documentEn
                                    const titleText =
                                        lang === 'th' ? file.nameTh || file.nameEn : file.nameEn || file.nameTh

                                    return (
                                        <Grid item xs={12} key={file.id}>
                                            <Grid container spacing={1} alignItems="center" className={classes.rowCard}>
                                                <Grid item>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="default"
                                                        component="a"
                                                        href={th?.path || '#'}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        disabled={!th?.path}
                                                    >
                                                        TH
                                                    </Button>
                                                </Grid>
                                                <Grid item>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="default"
                                                        component="a"
                                                        href={en?.path || '#'}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        disabled={!en?.path}
                                                    >
                                                        EN
                                                    </Button>
                                                </Grid>

                                                <Grid item xs>
                                                    <Typography
                                                        className={classes.sourceLabel}
                                                        variant="subtitle2"
                                                        gutterBottom
                                                    >
                                                        {translate.get('LABEL.DOCUMENT_NAME')}
                                                    </Typography>
                                                    <Typography
                                                        className={classes.fileLabel}
                                                        variant="subtitle1"
                                                        gutterBottom
                                                    >
                                                        {titleText}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )
                                })}
                            </Grid>
                        </MyAccordionCtrl>
                    </Box>
                )
            })}
        </Box>
    )
}

const useStyles = makeStyles(() => ({
    titleRow: { width: '100%', paddingRight: 12 },
    groupTitle: { fontWeight: 700 },
    guideTitle: { fontWeight: 700, marginBottom: 8 },
    guide: {
        padding: 12,
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        background: '#fafafa',
        overflowWrap: 'anywhere',
    },
    rowCard: {
        padding: 12,
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        background: '#fff',
    },
    sourceLabel: { color: '#9aa1b2', fontSize: 12, margin: 0 },
    fileLabel: { margin: 0 },
}))

export default GroupedSopList
